import React from 'react';
import { Link } from 'react-router-dom';
import { X, Plus, Minus, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { imageFallback, resolveImageUrl } from '../lib/imageUtils';

const CartPage = () => {
  const { cart, removeFromCart, updateQuantity, getCartTotal, getCartCount } = useCart();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center" data-testid="empty-cart">
        <div className="text-center space-y-6 max-w-md px-6">
          <ShoppingBag size={64} className="text-[#A3A3A3] mx-auto" />
          <h2 className="text-3xl font-light tracking-tighter text-white">Your Cart is Empty</h2>
          <p className="text-[#A3A3A3]">Discover our exquisite collection of jewelry pieces</p>
          <Link
            to="/products"
            className="inline-block px-10 py-4 bg-[#D4AF37] text-black font-medium tracking-wide uppercase text-sm hover:bg-[#F1E5AC] transition-all duration-300"
            data-testid="shop-now-button"
          >
            Shop Now
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-12">
          <h1 className="text-4xl sm:text-5xl font-light tracking-tighter text-white mb-2" data-testid="cart-page-title">
            Shopping Cart
          </h1>
          <p className="text-[#A3A3A3]">{getCartCount()} {getCartCount() === 1 ? 'item' : 'items'}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item) => {
              const imageUrl = resolveImageUrl(item.images?.[0], BACKEND_URL);

              return (
                <div
                  key={item.id}
                  className="bg-[#141414] border border-white/10 p-6 flex flex-col sm:flex-row gap-6"
                  data-testid={`cart-item-${item.id}`}
                >
                  <div className="w-full sm:w-32 aspect-[4/5] bg-black overflow-hidden flex-shrink-0">
                    <img src={imageUrl} alt={item.name} onError={imageFallback} className="w-full h-full object-cover" />
                  </div>

                  <div className="flex-1 space-y-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] mb-1">
                          {item.subcategory_name || item.category_name}
                        </p>
                        <h3 className="text-xl font-light text-white" data-testid={`cart-item-name-${item.id}`}>
                          {item.name}
                        </h3>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="text-[#A3A3A3] hover:text-white transition-colors"
                        data-testid={`remove-cart-item-${item.id}`}
                      >
                        <X size={20} />
                      </button>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="w-8 h-8 bg-black border border-white/20 hover:border-white flex items-center justify-center transition-colors"
                          data-testid={`decrease-cart-quantity-${item.id}`}
                        >
                          <Minus size={14} />
                        </button>
                        <span className="text-white w-8 text-center" data-testid={`cart-quantity-${item.id}`}>
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-8 h-8 bg-black border border-white/20 hover:border-white flex items-center justify-center transition-colors"
                          data-testid={`increase-cart-quantity-${item.id}`}
                        >
                          <Plus size={14} />
                        </button>
                      </div>

                      <div className="text-right">
                        <p className="text-xl font-light text-[#D4AF37]" data-testid={`cart-item-total-${item.id}`}>
                          ₹{(item.final_price * item.quantity).toLocaleString()}
                        </p>
                        {item.discount_value > 0 && (
                          <p className="text-sm text-[#A3A3A3] line-through">
                            ₹{(item.price * item.quantity).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="lg:col-span-1">
            <div className="bg-[#141414] border border-white/10 p-8 space-y-6 sticky top-24">
              <h2 className="text-2xl font-light tracking-tighter text-white">Order Summary</h2>

              <div className="space-y-3 py-6 border-y border-white/10">
                <div className="flex justify-between text-[#A3A3A3]">
                  <span>Subtotal</span>
                  <span data-testid="cart-subtotal">₹{getCartTotal().toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-[#A3A3A3]">
                  <span>Shipping</span>
                  <span>FREE</span>
                </div>
              </div>

              <div className="flex justify-between text-xl">
                <span className="text-white">Total</span>
                <span className="text-[#D4AF37] font-light" data-testid="cart-total">
                  ₹{getCartTotal().toLocaleString()}
                </span>
              </div>

              <Link
                to="/checkout"
                className="block w-full px-10 py-4 bg-[#D4AF37] text-black font-medium tracking-wide uppercase text-sm hover:bg-[#F1E5AC] transition-all duration-300 text-center"
                data-testid="proceed-to-checkout-button"
              >
                Proceed to Checkout
              </Link>

              <Link
                to="/products"
                className="block w-full px-10 py-4 bg-transparent text-white border border-white/20 hover:border-white font-medium tracking-wide uppercase text-sm transition-all duration-300 text-center"
                data-testid="continue-shopping-button"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
